import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { assignmentsAPI } from '../api/assignments'
import type { SurveyAssignment } from '../api/assignments'
import { Button, Card, CardHeader, Badge } from './ui'

interface OverdueSectionProps {
  orgId: string
}

export const OverdueSection: React.FC<OverdueSectionProps> = ({ orgId }) => {
  const intl = useIntl()
  const [overdue, setOverdue] = useState<SurveyAssignment[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadOverdue()
  }, [orgId])

  const loadOverdue = async () => {
    try {
      setLoading(true)
      const data = await assignmentsAPI.getOverdue(orgId)
      setOverdue(data)
    } catch {
      // Silently fail — section just won't show
    } finally {
      setLoading(false)
    }
  }

  const handleExtend = async (a: SurveyAssignment) => {
    try {
      await assignmentsAPI.extendDueDate(orgId, a.survey_id, a.member_id, 7)
      await loadOverdue()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : intl.formatMessage({ id: 'overdue.error.extend' }))
    }
  }

  const handleWaive = async (a: SurveyAssignment) => {
    if (!confirm(intl.formatMessage({ id: 'overdue.confirm.waive' }))) return
    try {
      await assignmentsAPI.waiveAssignment(orgId, a.survey_id, a.member_id)
      await loadOverdue()
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : intl.formatMessage({ id: 'overdue.error.waive' }))
    }
  }

  const handleRemind = async (a: SurveyAssignment) => {
    try {
      await assignmentsAPI.sendReminder(orgId, a.survey_id, a.member_id)
      alert(intl.formatMessage({ id: 'overdue.reminderSent' }))
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : intl.formatMessage({ id: 'overdue.error.remind' }))
    }
  }

  if (loading || overdue.length === 0) return null

  return (
    <Card elevated className="mb-6 border-l-4 border-l-error">
      <CardHeader
        title={intl.formatMessage({ id: 'overdue.title' }, { count: overdue.length })}
        subtitle={intl.formatMessage({ id: 'overdue.subtitle' })}
      />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="overdue.col.member" /></th>
              <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="overdue.col.survey" /></th>
              <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="overdue.col.daysOverdue" /></th>
              <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="overdue.col.remindersSent" /></th>
              <th className="pb-3 font-semibold text-foreground text-right"><FormattedMessage id="overdue.col.actions" /></th>
            </tr>
          </thead>
          <tbody>
            {overdue.map((a) => (
              <tr key={a.id} className="border-b border-border last:border-0">
                <td className="py-3">
                  <div className="font-medium text-foreground">{a.member_name || '—'}</div>
                  <div className="text-xs text-foreground-muted">{a.member_email}</div>
                </td>
                <td className="py-3 text-foreground">{a.survey_title || a.survey_id}</td>
                <td className="py-3">
                  <Badge variant="error"><FormattedMessage id="overdue.daysValue" values={{ count: a.days_overdue ?? 0 }} /></Badge>
                </td>
                <td className="py-3 text-foreground-muted">{a.reminders_sent}/3</td>
                <td className="py-3 text-right">
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => handleExtend(a)}>
                      <FormattedMessage id="overdue.extend" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleWaive(a)}>
                      <FormattedMessage id="overdue.waive" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleRemind(a)}>
                      <FormattedMessage id="overdue.remind" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}
